import React from 'react';
import './ReconnectLoopRuleVisual.css';

/**
 * Visual explainer for the reconnect_loop detection rule, rendered inside the
 * cause help modal ("?" on the Reconnect Loop Failure card).
 *
 * Both thresholds live in backend/reports/goggles_connectivity/causes.js
 * (RECONNECT_LOOP_GAP_SEC, RECONNECT_LOOP_DISCONNECT_SPAN_SEC). If either
 * changes, update RULES and the example durations below to match — this is the
 * user-facing statement of the same rule.
 */

const CONNECTION_LIMIT = '5 minutes';
const DISCONNECT_LIMIT = '1 hour';
const POWER_OFF_LIMIT = '15 seconds';
const OTA_REBOOT_LIMIT = '15 seconds';

// Each example is a vertical timeline: alternating event rows (D / R) and the
// leg between them. `sincePrevDisconnect` hangs off a Disconnect row and is the
// rule that was missing until 2026-09-07 — see the cause doc.
const EXAMPLES = [
  {
    verdict: 'pass',
    badge: 'Counts as a bug',
    summary: 'Two brief connections, minutes apart. This is the pattern we are looking for.',
    rows: [
      { event: 'D', time: '12:39:38' },
      { leg: 'off', label: 'disconnected 2m 59s' },
      { event: 'R', time: '12:42:37' },
      { leg: 'on', label: 'connected 41s', rule: `under ${CONNECTION_LIMIT}`, ok: true },
      { event: 'D', time: '12:43:18', sincePrevDisconnect: '3m 40s', ok: true },
      { leg: 'off', label: 'disconnected 38m 56s' },
      { event: 'R', time: '13:22:14' },
      { leg: 'on', label: 'connected 18s', rule: `under ${CONNECTION_LIMIT}`, ok: true },
      { event: 'D', time: '13:22:32', sincePrevDisconnect: '39m 14s', ok: true },
    ],
  },
  {
    verdict: 'fail',
    badge: 'Not a bug',
    summary:
      'Both connections were short, but they were hours apart — someone wearing the goggles briefly on separate occasions, not a connection that keeps dropping.',
    rows: [
      { event: 'D', time: 'Mon 13:22' },
      { leg: 'off', label: 'disconnected 16h 35m' },
      { event: 'R', time: 'Tue 05:57' },
      { leg: 'on', label: 'connected 17s', rule: `under ${CONNECTION_LIMIT}`, ok: true },
      { event: 'D', time: 'Tue 05:58', sincePrevDisconnect: '16h 35m', ok: false },
      { leg: 'off', label: 'disconnected 2h 19m' },
      { event: 'R', time: 'Tue 08:17' },
      { leg: 'on', label: 'connected 1m 21s', rule: `under ${CONNECTION_LIMIT}`, ok: true },
      { event: 'D', time: 'Tue 08:19', sincePrevDisconnect: '2h 19m', ok: false },
    ],
  },
  {
    verdict: 'fail',
    badge: 'Not a bug',
    summary: 'The connection held for a normal length of time, so the chain breaks.',
    rows: [
      { event: 'D', time: '09:14:02' },
      { leg: 'off', label: 'disconnected 1m 10s' },
      { event: 'R', time: '09:15:12' },
      { leg: 'on', label: 'connected 12m 40s', rule: `over ${CONNECTION_LIMIT}`, ok: false },
      { event: 'D', time: '09:27:52', sincePrevDisconnect: '13m 50s', ok: true },
    ],
  },
];

const EventRow = ({ row }) => (
  <div className="rlv-row rlv-row-event">
    <span className={`rlv-marker rlv-marker-${row.event === 'D' ? 'd' : 'r'}`}>{row.event}</span>
    <span className="rlv-event-label">
      {row.event === 'D' ? 'Disconnected' : 'Reconnected'}
      <span className="rlv-time">{row.time}</span>
    </span>
    {row.sincePrevDisconnect && (
      <span className={`rlv-chip ${row.ok ? 'ok' : 'bad'}`}>
        {row.ok ? '✓' : '✗'} {row.sincePrevDisconnect} since previous disconnect
      </span>
    )}
  </div>
);

const LegRow = ({ row }) => (
  <div className="rlv-row rlv-row-leg">
    <span className={`rlv-connector rlv-connector-${row.leg}`} />
    <span className={`rlv-leg-label rlv-leg-${row.leg}`}>{row.label}</span>
    {row.rule && (
      <span className={`rlv-chip ${row.ok ? 'ok' : 'bad'}`}>
        {row.ok ? '✓' : '✗'} {row.rule}
      </span>
    )}
  </div>
);

const ReconnectLoopRuleVisual = () => (
  <div className="rlv">
    <div className="rlv-rules">
      <div className="rlv-rule">
        <span className="rlv-rule-num">1</span>
        <div>
          <strong>Each connection was short</strong>
          <span className="rlv-rule-sub">
            Every reconnect dropped again within {CONNECTION_LIMIT}.
          </span>
        </div>
      </div>
      <div className="rlv-rule">
        <span className="rlv-rule-num">2</span>
        <div>
          <strong>The drops were back to back</strong>
          <span className="rlv-rule-sub">
            Each disconnect came within {DISCONNECT_LIMIT} of the one before it.
          </span>
        </div>
      </div>
    </div>
    <p className="rlv-note">
      Both must hold across two cycles in a row. Rule 1 alone is not enough — without rule 2, three
      brief wearings spread over a day look identical to a connection that keeps dropping.
    </p>
    <p className="rlv-note">
      Disconnects caused by the goggles being powered off are ignored — if the goggles announced a
      shutdown within {POWER_OFF_LIMIT} before the disconnect, that disconnect is left out of the
      chain. The connections on either side of it are treated as one.
    </p>
    <p className="rlv-note">
      The reboot after a firmware update is ignored the same way: a disconnect within{' '}
      {OTA_REBOOT_LIMIT} after a successful update is left out of the chain.
    </p>

    {EXAMPLES.map((ex, i) => (
      <div key={i} className={`rlv-example rlv-example-${ex.verdict}`}>
        <div className="rlv-example-head">
          <span className={`rlv-badge rlv-badge-${ex.verdict}`}>{ex.badge}</span>
          <span className="rlv-example-summary">{ex.summary}</span>
        </div>
        <div className="rlv-timeline">
          {ex.rows.map((row, j) =>
            row.event ? <EventRow key={j} row={row} /> : <LegRow key={j} row={row} />
          )}
        </div>
      </div>
    ))}
  </div>
);

export default ReconnectLoopRuleVisual;
